<script setup>
import { reactive, ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import OrdersApi from 'dashboard/api/orders';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const ORDER_STATUSES = [
  'pending',
  'processing',
  'partially-shipped',
  'shipped',
  'completed',
  'canceled',
];
const PAYMENT_STATUSES = ['pending', 'captured', 'failed'];
const PAYMENT_METHODS = ['pix_dynamic_code', 'boleto', 'payment_link'];

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const orders = ref([]);
const isLoading = ref(true);
const selectedInboxId = computed(() => route.params.inboxId);
const filters = reactive({
  order_status: '',
  payment_status: '',
  payment_method: '',
  provider_name: '',
  has_error: '',
});

const inboxes = useMapGetter('inboxes/getInboxes');
const getInboxName = inboxId => {
  const inbox = inboxes.value?.find(i => i.id === Number(inboxId));
  return inbox ? inbox.name : '';
};

const activeFilters = computed(() => {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '')
  );
});

async function fetchOrders() {
  try {
    isLoading.value = true;
    const response = await OrdersApi.get(
      selectedInboxId.value,
      activeFilters.value
    );
    orders.value = response.data?.payload || response.data || [];
  } catch (error) {
    useAlert(t('ORDERS_PAGE.ERRORS.LOAD_FAILED'));
  } finally {
    isLoading.value = false;
  }
}

onMounted(fetchOrders);

watch(() => [selectedInboxId.value, { ...activeFilters.value }], fetchOrders);

function navigateToConversation(order) {
  if (!order.conversation_id) return;

  router.push({
    name: 'inbox_conversation',
    params: {
      conversation_id: order.conversation_id,
    },
  });
}

function viewOrderDetails(order) {
  router.push({
    name: 'orders_payments_detail',
    params: {
      inboxId: selectedInboxId.value,
      orderId: order.id,
    },
  });
}

function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(amount) / 100);
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

function statusLabel(status) {
  const labels = {
    pending: () => t('ORDERS_PAGE.STATUS.PENDING'),
    processing: () => t('ORDERS_PAGE.STATUS.PROCESSING'),
    'partially-shipped': () => t('ORDERS_PAGE.STATUS.PARTIALLY_SHIPPED'),
    shipped: () => t('ORDERS_PAGE.STATUS.SHIPPED'),
    completed: () => t('ORDERS_PAGE.STATUS.COMPLETED'),
    canceled: () => t('ORDERS_PAGE.STATUS.CANCELED'),
    captured: () => t('ORDERS_PAGE.STATUS.CAPTURED'),
    failed: () => t('ORDERS_PAGE.STATUS.FAILED'),
  };
  return (labels[status] || labels.pending)();
}

function paymentMethodLabel(method) {
  const labels = {
    pix_dynamic_code: () => t('ORDERS_PAGE.PAYMENT_METHODS.PIX_DYNAMIC_CODE'),
    boleto: () => t('ORDERS_PAGE.PAYMENT_METHODS.BOLETO'),
    payment_link: () => t('ORDERS_PAGE.PAYMENT_METHODS.PAYMENT_LINK'),
  };
  return labels[method] ? labels[method]() : method;
}

function paymentMethodsLabel(order) {
  const methods = order.payment_methods || [];
  if (!methods.length) return '-';
  return methods.map(paymentMethodLabel).join(', ');
}

function orderStatusBadgeClass(status) {
  const normalized = String(status || '').toLowerCase();
  if (['completed', 'shipped'].includes(normalized)) {
    return 'text-n-green-11 bg-n-green-3';
  }
  if (normalized === 'canceled') return 'text-n-ruby-11 bg-n-ruby-3';
  if (['processing', 'partially-shipped'].includes(normalized)) {
    return 'text-n-blue-11 bg-n-blue-3';
  }
  return 'text-n-amber-11 bg-n-amber-3';
}

function paymentStatusBadgeClass(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'captured') return 'text-n-green-11 bg-n-green-3';
  if (normalized === 'failed') return 'text-n-ruby-11 bg-n-ruby-3';
  return 'text-n-amber-11 bg-n-amber-3';
}

const goBack = () => {
  router.push({ name: 'orders_payments_index' });
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 bg-n-surface-1">
      <div class="w-full max-w-6xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <div class="flex items-center gap-2">
            <Button
              icon="i-lucide-arrow-left"
              size="sm"
              variant="ghost"
              color-scheme="secondary"
              @click="goBack"
            />
            <span class="text-heading-1 text-n-slate-12">
              {{ t('ORDERS_PAGE.TITLE') }}
              <span
                v-if="selectedInboxId"
                class="text-n-slate-11 font-normal text-body-base ml-2"
              >
                {{
                  t('ORDERS_PAGE.INBOX_PREFIX', {
                    inbox: getInboxName(selectedInboxId),
                  })
                }}
              </span>
            </span>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-5 gap-3 pb-4">
          <select v-model="filters.order_status" class="input">
            <option value="">
              {{ t('ORDERS_PAGE.FILTERS.ORDER_STATUS') }}
            </option>
            <option
              v-for="status in ORDER_STATUSES"
              :key="status"
              :value="status"
            >
              {{ statusLabel(status) }}
            </option>
          </select>
          <select v-model="filters.payment_status" class="input">
            <option value="">
              {{ t('ORDERS_PAGE.FILTERS.PAYMENT_STATUS') }}
            </option>
            <option
              v-for="status in PAYMENT_STATUSES"
              :key="status"
              :value="status"
            >
              {{ statusLabel(status) }}
            </option>
          </select>
          <select v-model="filters.payment_method" class="input">
            <option value="">
              {{ t('ORDERS_PAGE.FILTERS.PAYMENT_METHOD') }}
            </option>
            <option
              v-for="method in PAYMENT_METHODS"
              :key="method"
              :value="method"
            >
              {{ paymentMethodLabel(method) }}
            </option>
          </select>
          <input
            v-model="filters.provider_name"
            class="input"
            type="search"
            :placeholder="t('ORDERS_PAGE.FILTERS.PROVIDER')"
          />
          <select v-model="filters.has_error" class="input">
            <option value="">{{ t('ORDERS_PAGE.FILTERS.HAS_ERROR') }}</option>
            <option value="true">{{ t('ORDERS_PAGE.FILTERS.YES') }}</option>
          </select>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-6xl mx-auto py-4">
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <div v-else-if="orders.length > 0" class="divide-y divide-n-weak">
          <div
            v-for="order in orders"
            :key="order.id || order.ref_id"
            class="grid grid-cols-1 lg:grid-cols-[1fr_12rem_12rem_12rem] gap-3 items-center py-4"
          >
            <div class="flex items-center gap-4 min-w-0">
              <div
                class="flex items-center justify-center size-10 rounded-lg bg-n-blue-2"
              >
                <span class="i-lucide-shopping-bag size-5 text-n-blue-11" />
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <h3 class="text-sm font-medium text-n-slate-12 truncate">
                    {{ order.ref_id || `#${order.id}` }}
                  </h3>
                  <span
                    v-if="order.has_error"
                    class="i-lucide-triangle-alert size-4 text-n-ruby-10"
                  />
                </div>
                <p class="text-xs text-n-slate-9 mt-0.5">
                  {{ order.contact_name || t('ORDERS_PAGE.UNKNOWN_CUSTOMER') }}
                  {{ t('ORDERS_PAGE.SEPARATOR') }}
                  {{ formatCurrency(order.total_amount) }}
                  {{ t('ORDERS_PAGE.SEPARATOR') }}
                  {{ formatDate(order.created_at) }}
                </p>
              </div>
            </div>

            <div class="flex flex-wrap gap-1.5">
              <span
                :class="
                  orderStatusBadgeClass(order.order_status || order.status)
                "
                class="px-2 py-0.5 text-xs font-medium rounded-full"
              >
                {{ statusLabel(order.order_status || order.status) }}
              </span>
              <span
                :class="paymentStatusBadgeClass(order.payment_status)"
                class="px-2 py-0.5 text-xs font-medium rounded-full"
              >
                {{ statusLabel(order.payment_status) }}
              </span>
            </div>

            <div class="text-xs text-n-slate-11">
              {{ paymentMethodsLabel(order) }}
              <span v-if="order.provider_name">
                {{ t('ORDERS_PAGE.SEPARATOR') }} {{ order.provider_name }}
              </span>
            </div>

            <div class="flex items-center justify-end gap-1">
              <Button
                :label="t('ORDERS_PAGE.ACTIONS.VIEW_DETAILS')"
                icon="i-lucide-receipt"
                size="xs"
                variant="ghost"
                color-scheme="secondary"
                @click="viewOrderDetails(order)"
              />
              <Button
                v-if="order.conversation_id"
                :label="t('ORDERS_PAGE.VIEW_IN_CONVERSATION')"
                icon="i-lucide-message-circle"
                size="xs"
                variant="ghost"
                color-scheme="secondary"
                @click="navigateToConversation(order)"
              />
            </div>
          </div>
        </div>

        <div
          v-else
          class="flex flex-col items-center justify-center py-20 text-center"
        >
          <div
            class="flex items-center justify-center w-16 h-16 rounded-full bg-n-alpha-black2 mb-4"
          >
            <span class="i-lucide-shopping-bag text-3xl text-n-slate-9" />
          </div>
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
            {{ t('ORDERS_PAGE.EMPTY_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-6 max-w-md">
            {{ t('ORDERS_PAGE.EMPTY_DESC') }}
          </p>
        </div>
      </div>
    </main>
  </section>
</template>
